import { useCallback, useEffect, useState } from 'react';
import { Promotion, fetchPromotionDetails } from 'src/services/shop/cart';

import { readCookie } from 'src/utils/Cookies';
import { getField, isEntry } from 'src/utils/EntryUtil';
import { useCart } from '../Cart/UseCart';

import { addBreadcrumb } from '@sentry/core';
import { Severity } from '@sentry/types';
import { useSalesChannel } from '../SalesChannel/SalesChannel';
import { DetailedPromotion } from './UseProgramPromotion';

export const useValidatedProgramPromotionInternal = () => {
  const { salesChannel } = useSalesChannel();
  const { removePromo, cart: { products, bundle } } = useCart();
  const [promotionDetails, setPromotionDetails] = useState<Promotion | undefined>();
  const [validatedProgramPromotion, setValidatedProgramPromotion] = useState<DetailedPromotion | undefined>();
  const [isProgramPromoLoading, setIsProgramPromoLoading] = useState(false);
  const [cartFdmpIds, setCartFdmpIds] = useState<string[]>([]);

  const promotionCode = readCookie('program');

  const cacheProgramDetails = useCallback(async (promoCode: string, resellerId: string) => {
    setIsProgramPromoLoading(true);
    try {
      const { data } = await fetchPromotionDetails({ resellerId, promoCode });
      setPromotionDetails(data);
      setIsProgramPromoLoading(false);
    } catch (e) {
      setIsProgramPromoLoading(false);
      addBreadcrumb({
        message: 'Error Fetching Program Promotion Details',
        data: {
          reseller: resellerId,
          promoCode,
        },
        category: 'promotions',
        level: Severity.Info,
      });
    }
  }, []);

  useEffect(() => {
    const resellerId = getField(salesChannel, 'cloverResellerId');
    if (!isEntry(salesChannel) || !promotionCode || !resellerId) return;
    cacheProgramDetails(promotionCode, resellerId);
  }, [promotionCode, salesChannel]);

  useEffect(() => {
    const allFdmpIdsInCart = [...products.map(p => p.fdmpId), ...(bundle?.hardware?.map(h => h.fdmpId) || [])];
    setCartFdmpIds(allFdmpIdsInCart);
  }, [products, bundle]);

  useEffect(() => {
    if (cartFdmpIds.length === 0) {
      setValidatedProgramPromotion(undefined);
      return;
    }

    const applicableFdmpIds = promotionDetails?.applicableToProductIds.map(p => p.productId) || [];
    const validCart = cartFdmpIds.some(cartFdmpId => applicableFdmpIds.includes(cartFdmpId));

    if (validCart) {
      setValidatedProgramPromotion(promotionDetails);
      return;
    }

    removePromo();
    setValidatedProgramPromotion(undefined);
  }, [promotionDetails, cartFdmpIds]);

  return { programPromotion: validatedProgramPromotion, isProgramPromoLoading };
};
